import { useEffect, useState } from "react";
import { AUTH_SCREENS } from "../../constant/constant";
import LoginPage from "./LoginPage";
import ForgotPasswordPage from "./ForgotPasswordPage";
import EmailSentPage from "./EmailSentPage";
import ResetPassword from "../ForgotPassword/ForgotPassword";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import useRecaptchaV3 from "../../hooks/recaptchaV3";
import { useTranslation } from "react-i18next";
import { getPath } from "../../helper/commonHelper";
import { useLoginHandlers } from "../Login/useLoginHandlers";
import { recaptchaSiteKey } from "../../utils/ApiHandler";
import { useAuth } from "../../provider/AuthContext";
import ProcessingLoader from "../../components/ProcessingLoader";
import { get } from "lodash";
import validator from "validator";
import { permissionsFn, redirectToPath } from "../../helper/com-helper";

/**
 * AuthRouter
 *
 * Top-level orchestrator for the auth flow.
 * Manages screen transitions and passes handlers down to individual pages.
 *
 * Screen flow:
 *   LOGIN ──[Forgot password?]──► FORGOT_PASSWORD ──[Submit]──► EMAIL_SENT
 *   EMAIL_SENT ──[Back to login]──► LOGIN
 *   FORGOT_PASSWORD ──[Back to login]──► LOGIN
 */
const initialLoginState = {
  email: "",
  password: "",
  showPassword: false,
};

const AuthRouter = () => {
  const [screen, setScreen] = useState(AUTH_SCREENS.LOGIN);
  let navigate = useNavigate();
  const dispatch = useDispatch();

  const [path] = useState(globalThis.location.href);
  // Reactive, unlike the old readAppValue("user") read this replaced: that
  // was a plain synchronous read with no subscription to anything, so this
  // component never re-rendered when the real session check (AuthContext's
  // own refresh(), driven by the session cookie) resolved after this
  // component's own first render — e.g. opening the app fresh in a new tab
  // with an already-valid session. It just kept showing the login form
  // forever, since nothing ever told it the user was, in fact, logged in.
  const { user, customPermissions, isAuthenticated, isLoading: authLoading } = useAuth();
  const { t } = useTranslation();
  const executeRecaptcha = useRecaptchaV3(recaptchaSiteKey);

  const [formData, setFormData] = useState(initialLoginState);

  const {
    submitUser,
    forgotPasswordSubmit,
    handleSSO,
    emailError,
    setEmailError,
    onOKTAButtonClick,
    onEffectFn,
  } = useLoginHandlers({
    dispatch,
    navigate,
    t,
    executeRecaptcha,
  });
  const handleClickShowPassword = () => {
    setFormData({
      ...formData,
      showPassword: !formData.showPassword,
    });
  };

  const handleChange = (prop) => (event) => {
    const value =
      prop === "email" ? event.target.value?.toLowerCase() : event.target.value;

    if (prop === "email") {
      if (value && !validator.isEmail(value)) {
        setEmailError("emailAddressValidation");
      } else {
        setEmailError("");
      }
    }

    setFormData({
      ...formData,
      [prop]: value,
    });
  };

  const { email, password, showPassword } = formData;

  const isDisabled = !email || !password || Boolean(emailError);

  useEffect(() => {
    if (authLoading) return;
    if (isAuthenticated) {
      // Must match the same permission set PrivateRoute gates routes with
      // (permissionsFn in com-helper.js merges in custom_permissions too) —
      // otherwise a user whose menu access comes entirely through
      // custom_permissions (e.g. ID/admin: CLIENT_VIEW, USER_VIEW, ...)
      // never matches any BAR_MENU node here, and getPath returns null.
      const permissions = permissionsFn({ ...user, custom_permissions: customPermissions });
      const roles = get(user, "roles", []);
      const path = getPath(permissions, roles);
      // Only navigate when a real destination resolves. This effect exists
      // for reopening an already-authenticated tab (isAuthenticated flips
      // true once refresh() resolves, well after this component's first
      // render) — but it also re-fires on a fresh in-tab login, racing
      // processLogin's own direct redirectToPath(path) call (which already
      // has the correct destination). Unconditionally calling
      // redirectToPath(null) here — e.g. from a render where `user`/
      // `customPermissions` haven't fully caught up yet — sent the browser
      // to "/" (redirectToPath's own null-safe fallback) right after
      // processLogin had already navigated somewhere real, undoing it. Doing
      // nothing when there's no resolved path yet is safe either way: a
      // later re-render with the real data fires this effect again.
      if (path) {
        redirectToPath(path);
      }
    }
  }, [authLoading, isAuthenticated, user, customPermissions]);

  useEffect(() => {
    onEffectFn(path, setScreen);
  }, [onEffectFn, path]);

  const resetForms = () => {
    setFormData(initialLoginState);
    setEmailError("");
    //  setIsSubmitting(false);
  };


  const goTo = (targetScreen) => {
    resetForms();
    setScreen(targetScreen);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  // While the session check is still in flight (e.g. a brand-new tab, before
  // AuthContext's cookie-driven refresh() has resolved), this used to render
  // the login form immediately regardless — then, the instant isAuthenticated
  // flipped true, the effect above fired and the screen abruptly swapped to
  // the destination. Showing a loader here instead avoids that jarring
  // flash-then-jump: an already-authenticated user just sees a brief loader
  // before landing on their destination, never the login form at all.
  if (authLoading) {
    return <ProcessingLoader />;
  }

  switch (screen) {
    case AUTH_SCREENS.FORGOT_PASSWORD:
      return (
        <ForgotPasswordPage
          forgotForm={formData}
          errors={emailError}
          isSubmitting={false}
          onFieldChange={() => handleChange("email")}
          onSubmit={() => forgotPasswordSubmit(formData.email)}
          onBackToLogin={() => goTo(AUTH_SCREENS.LOGIN)}
          onDidntReceive={() => { }}
        />
      );

    case AUTH_SCREENS.EMAIL_SENT:
      return (
        <EmailSentPage
          onBackToLogin={() => goTo(AUTH_SCREENS.LOGIN)}
        />
      );

    case AUTH_SCREENS.RESET_PASSWORD:
      return <ResetPassword />;

    case AUTH_SCREENS.LOGIN:
    default:
      return (
        <LoginPage
          loginForm={formData}
          errors={emailError}
          isLoginDisabled={isDisabled}
          isSubmitting={false}
          onFieldChange={handleChange}
          onTogglePassword={handleClickShowPassword}
          onSubmit={submitUser}
          onOktaLogin={onOKTAButtonClick}
          onMicrosoftLogin={handleSSO}
          onForgotPassword={() => goTo(AUTH_SCREENS.FORGOT_PASSWORD)}
        />
      );
  }
};

export default AuthRouter;
